import type { LoginModel } from '@/models/DTO/authentication/login.model'
import type { LoginResponseModel } from '@/models/DTO/authentication/login-response.model'
import type { ForgotPasswordModel } from '@/models/DTO/authentication/forgot-password.model'
import type { SetPasswordModel } from '@/models/DTO/authentication/set-password.model'
import type { ResetPasswordModel } from '@/models/DTO/authentication/reset-password.model'
import type { ChangePasswordModel } from '@/models/DTO/authentication/change-password.model'
import type { RegisterAdminModel } from '@/models/DTO/authentication/register-admin.model'
import type { RegisterVeterinarianModel } from '@/models/DTO/authentication/register-veterinarian.model'
import type { RequestChangeEmailModel } from '@/models/DTO/authentication/request-change-email.model'
import type { ConfirmChangeEmailModel } from '@/models/DTO/authentication/confirm-change-email.model'
import type { ConfirmChangeEmailResponseModel } from '@/models/DTO/authentication/confirm-change-email-response.model'
import type { CustomerLoginTokenRequestModel } from '@/models/DTO/authentication/customer-login-token-request.model'
import type { RegisterCustomerModel } from '@/models/DTO/customer/register-customer.model'
import type { Customer } from '@/models/DTO/customer/customer.model'
import { BaseAPIService, setRefreshTokenURL } from './base-api.service'

// API endpoint URLs
const API_VERSION: string = import.meta.env.VITE_API_VERSION
const ACCOUNT_URL_STUB = `/api/v${API_VERSION}/Account`
const LOGIN_URL = `${ACCOUNT_URL_STUB}/Login`
const CUSTOMER_LOGIN_URL = `${ACCOUNT_URL_STUB}/CustomerLogin`
const CUSTOMER_LOGIN_TOKEN_REQUEST_URL = `${ACCOUNT_URL_STUB}/CustomerLoginTokenRequest`
const LOGOUT_URL = `${ACCOUNT_URL_STUB}/Logout`
const REGISTER_ADMIN_URL = `${ACCOUNT_URL_STUB}/RegisterAdmin`
const REGISTER_VETERINARIAN_URL = `${ACCOUNT_URL_STUB}/RegisterVeterinarianUser`
const FORGOT_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ForgotPassword`
const SET_PASSWORD_URL = `${ACCOUNT_URL_STUB}/SetPassword`
const RESET_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ResetPassword`
const CHANGE_PASSWORD_URL = `${ACCOUNT_URL_STUB}/ChangePassword`
export const REFRESH_TOKEN_URL = `${ACCOUNT_URL_STUB}/RefreshToken`
const REVOKE_REFRESH_TOKEN_URL = `${ACCOUNT_URL_STUB}/RevokeRefreshToken`
const REQUEST_CHANGE_EMAIL_URL = `${ACCOUNT_URL_STUB}/RequestChangeEmail`
const CONFIRM_CHANGE_EMAIL_URL = `${ACCOUNT_URL_STUB}/ConfirmChangeEmail`
const REGISTER_CUSTOMER_URL = `${ACCOUNT_URL_STUB}/RegisterCustomer`
const REGISTER_CUSTOMER_IF_NEEDED_URL = `${ACCOUNT_URL_STUB}/RegisterCustomerIfNeeded`
const SET_ACCEPTED_TERMS_KEY = `${ACCOUNT_URL_STUB}/SetAcceptedTermsKey`

// Set the refresh token URL in the BaseAPIService (needed for 401 interceptor)
setRefreshTokenURL(REFRESH_TOKEN_URL)

export interface IAuthenticationService {
  login(model: LoginModel): Promise<LoginResponseModel>
  customerLogin(model: LoginModel): Promise<LoginResponseModel>
  customerLoginTokenRequest(model: CustomerLoginTokenRequestModel): Promise<void>
  refreshToken(): Promise<LoginResponseModel>
  revokeToken(): Promise<void>
  logout(): Promise<void>
  registerAdmin(model: RegisterAdminModel): Promise<void>
  registerVeterinarian(model: RegisterVeterinarianModel): Promise<void>
  registerCustomer(model: RegisterCustomerModel): Promise<Customer>
  registerCustomerIfNeeded(model: RegisterCustomerModel): Promise<Customer>
  forgotPassword(model: ForgotPasswordModel): Promise<void>
  setPassword(model: SetPasswordModel): Promise<void>
  resetPassword(model: ResetPasswordModel): Promise<void>
  changePassword(model: ChangePasswordModel): Promise<void>
  requestChangeEmail(model: RequestChangeEmailModel): Promise<void>
  confirmChangeEmail(model: ConfirmChangeEmailModel): Promise<ConfirmChangeEmailResponseModel>
  setAcceptedTerms(): Promise<void>
}

export class AuthenticationService implements IAuthenticationService {
  private readonly _baseAPIService: BaseAPIService

  constructor() {
    // Create BaseAPIService instance directly (no dependency injection)
    this._baseAPIService = new BaseAPIService()
  }

  public async login(model: LoginModel): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(LOGIN_URL, {
      ...model,
    })

    this._baseAPIService.setJWT(response.data.token)

    return {
      ...response.data,
    }
  }

  public async customerLoginTokenRequest(model: CustomerLoginTokenRequestModel): Promise<void> {
    await this._baseAPIService.client.post(CUSTOMER_LOGIN_TOKEN_REQUEST_URL, {
      ...model,
    })
  }

  public async customerLogin(model: LoginModel): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(
      CUSTOMER_LOGIN_URL,
      {
        ...model,
      },
    )

    this._baseAPIService.setJWT(response.data.token)

    return {
      ...response.data,
    }
  }

  public async refreshToken(): Promise<LoginResponseModel> {
    const response = await this._baseAPIService.client.post<LoginResponseModel>(REFRESH_TOKEN_URL)

    this._baseAPIService.setJWT(response.data.token)

    return {
      ...response.data,
    }
  }

  public async revokeToken(): Promise<void> {
    await this._baseAPIService.client.post(REVOKE_REFRESH_TOKEN_URL)
    this._baseAPIService.unsetJWT()
  }

  public async logout(): Promise<void> {
    await this._baseAPIService.client.post(LOGOUT_URL)
    this._baseAPIService.unsetJWT()
  }

  public async registerAdmin(model: RegisterAdminModel): Promise<void> {
    await this._baseAPIService.client.post(REGISTER_ADMIN_URL, {
      ...model,
    })
  }

  public async registerVeterinarian(model: RegisterVeterinarianModel): Promise<void> {
    await this._baseAPIService.client.post(REGISTER_VETERINARIAN_URL, {
      ...model,
    })
  }

  public async registerCustomer(model: RegisterCustomerModel): Promise<Customer> {
    const resp = await this._baseAPIService.client.post(REGISTER_CUSTOMER_URL, {
      ...model,
    })

    return resp.data
  }

  public async registerCustomerIfNeeded(model: RegisterCustomerModel): Promise<Customer> {
    const resp = await this._baseAPIService.client.post(REGISTER_CUSTOMER_IF_NEEDED_URL, {
      ...model,
    })

    return resp.data
  }

  public async forgotPassword(model: ForgotPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(FORGOT_PASSWORD_URL, {
      ...model,
    })
  }

  /**
   * This sets a password without needing the original password.
   * The new autogenerated password will be emailed to the user's
   * email address on file.
   */
  public async setPassword(model: SetPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(SET_PASSWORD_URL, {
      ...model,
    })
  }

  /**
   * This is the second step in the forgot password functionality.
   */
  public async resetPassword(model: ResetPasswordModel): Promise<void> {
    await this._baseAPIService.client.post(RESET_PASSWORD_URL, {
      ...model,
    })
  }

  public async changePassword(model: ChangePasswordModel): Promise<void> {
    await this._baseAPIService.client.post(CHANGE_PASSWORD_URL, {
      ...model,
    })
  }

  public async requestChangeEmail(model: RequestChangeEmailModel): Promise<void> {
    await this._baseAPIService.client.post(REQUEST_CHANGE_EMAIL_URL, {
      ...model,
    })
  }

  public async confirmChangeEmail(
    model: ConfirmChangeEmailModel,
  ): Promise<ConfirmChangeEmailResponseModel> {
    const resp = await this._baseAPIService.client.post<ConfirmChangeEmailResponseModel>(
      CONFIRM_CHANGE_EMAIL_URL,
      {
        ...model,
      },
    )

    return resp.data
  }

  public async setAcceptedTerms(): Promise<void> {
    await this._baseAPIService.client.get(SET_ACCEPTED_TERMS_KEY)
  }
}
